import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {FaCheck, FaDatabase, FaRegCopy, FaServer, FaTable, FaTerminal} from 'react-icons/fa';
import useFreeResourcesOverview from '../hooks/useFreeResourcesOverview';
import type {FreeResourceSize} from '../types/freeResources';
import '../styles/McpPage.scss';
import '../styles/FreeTierPage.scss';

// The free tier's own page (Milos, 2026-11-02: "It should just have a dedicated page with explanation"), in the MCP setup
// page's layout: what is included, then what to ask the agent. One prompt per thing redu MCP does with the free VMs
// (Milos, 2026-11-02: "Remove publish it part. And add instead. Add data to my database").
export const FREE_TIER_PROMPTS: {icon: React.ElementType; title: string; prompt: string}[] = [
    {icon: FaServer, title: 'Create your free VM', prompt: 'Create my free redu VM and deploy this project on it.'},
    {icon: FaDatabase, title: 'Add the private database', prompt: 'Create my free private PostgreSQL and connect my app to it.'},
    {icon: FaTable, title: 'Add data to your database', prompt: 'Add data to my database.'},
    {icon: FaTerminal, title: 'Get SSH access', prompt: 'Give me SSH access to my free VM.'},
];

/** The size the platform reports for a slot, or nothing while it is not known: never a number made up here. */
const sizeLabel = (size?: FreeResourceSize) =>
    size ? `${size.vcpus} vCPU · ${size.memory_mib / 1024} GiB RAM · ${size.total_disk_gib} GiB disk` : null;

const FreeTierPage: React.FC = () => {
    const {overview} = useFreeResourcesOverview();
    const [copied, setCopied] = useState<number | null>(null);
    const copy = (i: number) => {
        const done = () => { setCopied(i); setTimeout(() => setCopied(cur => (cur === i ? null : cur)), 2000); };
        if (typeof navigator !== 'undefined' && navigator.clipboard) navigator.clipboard.writeText(FREE_TIER_PROMPTS[i].prompt).then(done);
    };
    const vmSize = sizeLabel(overview?.slots.find(s => s.kind === 'website')?.size);
    const dbSize = sizeLabel(overview?.slots.find(s => s.kind === 'postgres')?.size);
    const allowance = Number(overview?.website_allowance?.bytes);
    const allowanceGb = Number.isFinite(allowance) && allowance > 0 ? Math.round(allowance / 1e9) : null;

    return (
        <div className="ai-page free-tier-page">
            <div className="ai-container">

                <header className="ai-hero">
                    <span className="ai-kicker">Free tier</span>
                    <h1>A real VM and a private database, free.</h1>
                    <p className="ai-sub">Included with your plan and always on. Your agent creates and runs them through redu MCP.</p>
                </header>

                <section className="ai-step" aria-labelledby="free-tier-what">
                    <h2 id="free-tier-what"><span className="ai-step-num">1</span> What you get</h2>
                    <div className="free-tier-specs">
                        <div className="free-tier-spec">
                            <FaServer className="free-tier-spec__icon" aria-hidden="true"/>
                            <strong>VM</strong>
                            {vmSize && <span>{vmSize}</span>}
                            <span>Its own HTTPS address and SSH</span>
                        </div>
                        <div className="free-tier-spec">
                            <FaDatabase className="free-tier-spec__icon" aria-hidden="true"/>
                            <strong>Private PostgreSQL</strong>
                            {dbSize && <span>{dbSize}</span>}
                            <span>Reachable only from your VM</span>
                        </div>
                    </div>
                    <p className="ai-note">
                        {allowanceGb ? `${allowanceGb} GB of public traffic a month. ` : ''}You can delete either one in{' '}
                        <Link to="/category/compute/instances?view=free" className="ai-link">Instances</Link>.
                    </p>
                </section>

                <section className="ai-step" aria-labelledby="free-tier-ask">
                    <h2 id="free-tier-ask"><span className="ai-step-num">2</span> Ask your agent</h2>
                    <div className="ai-ops">
                        {FREE_TIER_PROMPTS.map((op, i) => (
                            <button key={op.title} type="button" className="ai-op" onClick={() => copy(i)} aria-label={`${op.title}: copy "${op.prompt}"`}>
                                <span className="ai-op-head">
                                    <op.icon className="ai-op-icon" aria-hidden="true"/>
                                    <span className="ai-op-title">{op.title}</span>
                                    <span className="ai-op-copy" aria-hidden="true">{copied === i ? <FaCheck/> : <FaRegCopy/>}</span>
                                </span>
                                <span className="ai-op-prompt">{copied === i ? 'Copied' : `"${op.prompt}"`}</span>
                            </button>
                        ))}
                    </div>
                </section>

                <footer className="ai-foot">
                    <span>Not connected yet? <Link to="/mcp" className="ai-link">Connect your agent</Link></span>
                    <Link to="/category/compute/instances?view=free" className="ai-foot-link">See your free VMs</Link>
                </footer>

            </div>
        </div>
    );
};

export default FreeTierPage;
